/**
 * C8 Offline task complete / reopen (status-only queue).
 *
 * Rules (docs/engineering/offline_reference.md): one pending change per task (latest intent wins on the device;
 * toggling back to the status the device last saw cancels the change), last writer wins on the server,
 * archived-project and removed-task changes are rejected on sync and shown to the user. Replay uses the signed-in
 * user's own session through cp_apply_offline_task_status (SECURITY INVOKER: RLS and the A4 guards apply).
 * Each change carries a mutation_id so a retried replay is never applied twice. Changes queued by another user
 * on this device are never replayed under the current user's session.
 */
import { supabase } from "../../supabase";
import { readOrgScopedJson, writeOrgScopedJson } from "../orgCacheStorage";

export const PENDING_TASK_STATUS_KEY = "CP_PENDING_TASK_STATUS_V1";
export const TASK_STATUS_ATTENTION_KEY = "CP_TASK_STATUS_ATTENTION_V1";

const DONE = new Set(["applied", "already_applied", "duplicate"]);
const REFUSED_MESSAGES = Object.freeze({
  project_closed: "The project was archived before your change synced. The task was not changed.",
  task_removed: "The task was deleted before your change synced.",
  not_found: "You no longer have access to this task. Your change was not saved.",
  not_permitted: "You no longer have permission to change this task. Your change was not saved.",
});

function orgOf(actor) {
  return actor?.organization_id || null;
}

function read(key, actor) {
  try {
    const value = readOrgScopedJson(key, orgOf(actor));
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
}

function write(key, actor, list) {
  writeOrgScopedJson(key, orgOf(actor), list);
}

function newMutationId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  // RFC4122 v4 fallback
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

export function listPendingTaskStatus(actor) {
  return read(PENDING_TASK_STATUS_KEY, actor);
}

export function listTaskStatusAttention(actor) {
  return read(TASK_STATUS_ATTENTION_KEY, actor);
}

export function dismissTaskStatusAttention(actor, mutationId) {
  write(TASK_STATUS_ATTENTION_KEY, actor, listTaskStatusAttention(actor).filter((x) => x.mutation_id !== mutationId));
}

/** Queue a complete / reopen made while offline. Returns the task as the device should now show it. */
export function queueTaskStatus(actor, task, status) {
  if (!task?.project_task_id) throw new Error("Task is required.");
  const pending = listPendingTaskStatus(actor);
  const existing = pending.find((x) => x.project_task_id === task.project_task_id);
  const rest = pending.filter((x) => x.project_task_id !== task.project_task_id);
  const baseStatus = existing ? existing.base_status : task.status;
  const next =
    baseStatus === status
      ? rest // toggled back to what the server last showed: nothing to sync
      : [
          ...rest,
          {
            mutation_id: newMutationId(),
            project_task_id: task.project_task_id,
            project_id: task.project_id || null,
            title: task.title || "",
            status,
            base_status: baseStatus,
            user_id: actor?.supabase_user_id || null,
            queued_at: new Date().toISOString(),
          },
        ];
  write(PENDING_TASK_STATUS_KEY, actor, next);
  return { ...task, status, pendingSync: baseStatus !== status };
}

/** Drop a queued change for a task (a newer online change supersedes it). */
export function discardPendingTaskStatus(actor, taskId) {
  const pending = listPendingTaskStatus(actor);
  const next = pending.filter((x) => x.project_task_id !== taskId);
  if (next.length !== pending.length) write(PENDING_TASK_STATUS_KEY, actor, next);
}

/** Overlay queued changes on tasks read from the device copy. */
export function applyPendingTaskStatus(actor, tasks) {
  const byTask = new Map(listPendingTaskStatus(actor).map((x) => [x.project_task_id, x]));
  return (tasks || []).map((t) => {
    const p = byTask.get(t.project_task_id);
    return p ? { ...t, status: p.status, pendingSync: true } : t;
  });
}

function isNetworkError(error) {
  const msg = String(error?.message || "").toLowerCase();
  return !error?.code && (msg.includes("fetch") || msg.includes("network") || msg.includes("timeout"));
}

/**
 * Replay queued changes for the signed-in user. Network/auth failures keep the item for the next attempt;
 * server refusals move it to the attention list. Returns counts.
 */
export async function drainPendingTaskStatus({ actor, client = supabase } = {}) {
  const userId = actor?.supabase_user_id;
  const summary = { applied: 0, refused: 0, kept: 0 };
  if (!userId) return summary;
  const pending = listPendingTaskStatus(actor);
  if (pending.length === 0) return summary;

  const keep = [];
  const attention = listTaskStatusAttention(actor);
  for (const item of pending) {
    if (item.user_id && item.user_id !== userId) {
      keep.push(item); // never replay another user's change under this session
      summary.kept += 1;
      continue;
    }
    let data;
    let error;
    try {
      ({ data, error } = await client.rpc("cp_apply_offline_task_status", {
        p_mutation_id: item.mutation_id,
        p_task_id: item.project_task_id,
        p_status: item.status,
      }));
    } catch (thrown) {
      error = thrown;
    }
    if (error) {
      const status = error.status ?? error.statusCode;
      if (isNetworkError(error) || status === 401 || status === 403 || String(error.code) === "PGRST301") {
        keep.push(item);
        summary.kept += 1;
        continue;
      }
      attention.push({ ...item, result: "error", message: error.message || "Your task change could not be saved." });
      summary.refused += 1;
      continue;
    }
    const result = data?.result;
    if (DONE.has(result)) {
      summary.applied += 1;
    } else {
      attention.push({ ...item, result: result || "error", message: REFUSED_MESSAGES[result] || "Your task change could not be saved." });
      summary.refused += 1;
    }
  }
  write(PENDING_TASK_STATUS_KEY, actor, keep);
  write(TASK_STATUS_ATTENTION_KEY, actor, attention);
  return summary;
}
